import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Calendar,
  Tag,
  Image,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Globe
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Form states
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: '',
    category: '',
    tags: '',
    status: 'draft',
    language: 'fr'
  });
  
  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', description: '' });

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    loadPosts();
    loadCategories();
  }, [page]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog/posts`, {
        params: { page, limit: 10 },
        headers,
        withCredentials: true
      });
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/categories`, {
        headers,
        withCredentials: true
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Titre et contenu requis');
      return;
    }

    try {
      const postData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };

      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.post_id}`, postData, {
          headers,
          withCredentials: true
        });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/admin/blog/posts`, postData, {
          headers,
          withCredentials: true
        });
        toast.success('Article créé');
      }

      setShowEditor(false);
      setEditingPost(null);
      resetForm();
      loadPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      image_url: post.image_url || '',
      category: post.category || '',
      tags: post.tags ? post.tags.join(', ') : '',
      status: post.status,
      language: post.language || 'fr'
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;

    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, {
        headers,
        withCredentials: true
      });
      toast.success('Article supprimé');
      loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    
    if (!newCategory.name || !newCategory.slug) {
      toast.error('Nom et slug requis');
      return;
    }

    try {
      await axios.post(`${API}/admin/blog/categories`, newCategory, {
        headers,
        withCredentials: true
      });
      toast.success('Catégorie créée');
      setShowCategoryForm(false);
      setNewCategory({ name: '', slug: '', description: '' });
      loadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;

    try {
      await axios.delete(`${API}/admin/blog/categories/${categoryId}`, {
        headers,
        withCredentials: true
      });
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      image_url: '',
      category: '',
      tags: '',
      status: 'draft',
      language: 'fr'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Editor Modal
  if (showEditor) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-2xl shadow-sm p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {editingPost ? 'Modifier l\'article' : 'Nouvel article'}
          </h2>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { setShowEditor(false); setEditingPost(null); resetForm(); }}
          >
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titre de l'article"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Langue</label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ht">Kreyòl</option>
                <option value="es">Español</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Extrait</label>
            <Input
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Court résumé de l'article"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Contenu *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Contenu de l'article (HTML supporté)"
              className="w-full px-3 py-2 border rounded-lg min-h-[300px] font-mono text-sm"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">URL de l'image</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Catégorie</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">-- Sélectionner --</option>
                {categories.map((cat) => (
                  <option key={cat.category_id} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tags (séparés par virgule)</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => { setShowEditor(false); setEditingPost(null); resetForm(); }}>
              Annuler
            </Button>
            <Button type="submit" className="bg-primary">
              <Save className="w-4 h-4 mr-2" />
              {editingPost ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold">Gestion du Blog</h2>
          <p className="text-gray-500 text-sm">{total} article(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCategoryForm(!showCategoryForm)}>
            <Tag className="w-4 h-4 mr-1" /> Catégories
          </Button>
          <Button onClick={() => { resetForm(); setShowEditor(true); }} className="bg-primary">
            <Plus className="w-4 h-4 mr-1" /> Nouvel Article
          </Button>
        </div>
      </div>

      {/* Category Management */}
      {showCategoryForm && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="font-semibold mb-3">Catégories</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((cat) => (
              <span 
                key={cat.category_id} 
                className="inline-flex items-center bg-white px-3 py-1 rounded-full text-sm border"
              >
                {cat.name}
                <button 
                  onClick={() => handleDeleteCategory(cat.category_id)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {categories.length === 0 && <span className="text-gray-400">Aucune catégorie</span>}
          </div>
          <form onSubmit={handleCreateCategory} className="flex gap-2">
            <Input
              placeholder="Nom"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="flex-1"
            />
            <Input
              placeholder="Slug (ex: actualites)"
              value={newCategory.slug}
              onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value.toLowerCase().replace(/\s/g, '-') })}
              className="flex-1"
            />
            <Button type="submit" size="sm">Ajouter</Button>
          </form>
        </div>
      )}

      {/* Posts List */}
      <div className="bg-white rounded-2xl shadow-sm">
        <div className="p-4 border-b flex justify-between items-center">
          <span className="text-sm text-gray-500">Page {page} sur {totalPages || 1}</span>
          <Button variant="outline" size="sm" onClick={loadPosts}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          </div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun article</p>
            <Button onClick={() => setShowEditor(true)} className="mt-4">
              <Plus className="w-4 h-4 mr-1" /> Créer le premier article
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {posts.map((post) => (
              <div key={post.post_id} className="p-4 hover:bg-gray-50 flex items-center gap-4">
                {post.image_url && (
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{post.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {post.status === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                    {post.category && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {post.category}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {post.language?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(post.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(post)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(post.post_id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-500">Page {page} / {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BlogManager;
